import React, { useState, useMemo, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import AIInsightCard from './AIInsightCard';
import { useTranslation } from 'react-i18next';

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#64748b'];

const formatCurrency = (amount, currencyCode, locale) => {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode || 'CRC' }).format(amount);
};

// Helper para sanitizar fechas DD-MM-YYYY o DD/MM/YYYY a YYYY-MM-DD (ISO).
const parseIsoDate = (dateStr) => {
  if (!dateStr) return null;
  // Detecta exactamente DD-MM-YYYY o DD/MM/YYYY
  const match = String(dateStr).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (match) {
    const day   = match[1];
    const month = match[2];
    const year  = match[3];
    return `${year}-${month}-${day}`; // Fuerza YYYY-MM-DD
  }
  return dateStr;
};

const SpendingBreakdown = ({ payments, insightText }) => {
  const { t, i18n } = useTranslation();
  const [timeRange, setTimeRange] = useState('all'); // 'all', 'this_month', 'last_month'
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Filtrar pagos según el rango de tiempo seleccionado
  const timeFilteredPayments = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return payments.filter(payment => {
      // Si no tiene fecha, solo lo incluimos en "Todo el Historial"
      if (!payment.due_date) return timeRange === 'all';

      // Ajustamos la fecha del pago (asumiendo YYYY-MM-DD)
      const paymentDate = new Date(`${payment.due_date}T00:00:00`);
      const paymentMonth = paymentDate.getMonth();
      const paymentYear = paymentDate.getFullYear();

      if (timeRange === 'this_month') {
        return paymentMonth === currentMonth && paymentYear === currentYear;
      } else if (timeRange === 'last_month') {
        // Calcular el mes pasado correctamente (maneja cambio de año)
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return paymentMonth === lastMonthDate.getMonth() && paymentYear === lastMonthDate.getFullYear();
      }
      return true; // 'all'
    });
  }, [payments, timeRange]);

  // Extraer las monedas disponibles de los pagos filtrados por tiempo
  const availableCurrencies = useMemo(() => {
    const currencies = timeFilteredPayments.map(p => p.currency || 'CRC');
    const unique = [...new Set(currencies)];
    return unique.length > 0 ? unique : ['CRC'];
  }, [timeFilteredPayments]);

  const [activeCurrency, setActiveCurrency] = useState(availableCurrencies[0] || 'CRC');

  // Asegurar que la moneda activa sea válida si los pagos cambian
  useEffect(() => {
    if (availableCurrencies.length > 0 && !availableCurrencies.includes(activeCurrency)) {
      setActiveCurrency(availableCurrencies[0]);
    }
  }, [availableCurrencies, activeCurrency]);

  // Reset selected category when timeRange or currency changes
  useEffect(() => {
    setSelectedCategory(null);
  }, [timeRange, activeCurrency]);

  const filteredPayments = useMemo(() => {
    return timeFilteredPayments.filter(p => (p.currency || 'CRC') === activeCurrency);
  }, [timeFilteredPayments, activeCurrency]);

  let data = [];
  let total = 0;

  // Agrupamiento 100% Client-Side usando raw category keys para mantener la consistencia
  const groupedData = filteredPayments.reduce((acc, payment) => {
    if (!payment.amount || parseFloat(payment.amount) <= 0) return acc;
    const category = (payment.spending_category || 'OTHERS').toUpperCase().trim();
    if (!acc[category]) acc[category] = 0;
    acc[category] += parseFloat(payment.amount);
    return acc;
  }, {});

  data = Object.keys(groupedData)
    .map(key => ({
      categoryKey: key,
      name: t(`categories.${key}`, key),
      value: groupedData[key]
    }))
    .sort((a, b) => b.value - a.value);

  total = data.reduce((sum, item) => sum + item.value, 0);

  // Helper para verificar correspondencia estricta del mes/año bajo visualización (usando due_date o created_at)
  const matchesVisualTimeRange = (payment, range) => {
    if (range === 'all') return true;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = lastMonthDate.getMonth();
    const lastYear = lastMonthDate.getFullYear();

    const checkDate = (dateStr) => {
      if (!dateStr) return false;
      const parsed = parseIsoDate(dateStr);
      if (!parsed) return false;
      try {
        const d = new Date(parsed.includes('T') ? parsed : `${parsed}T00:00:00`);
        const m = d.getMonth();
        const y = d.getFullYear();

        if (range === 'this_month') {
          return m === currentMonth && y === currentYear;
        } else if (range === 'last_month') {
          return m === lastMonth && y === lastYear;
        }
      } catch (e) {
        return false;
      }
      return false;
    };

    const dueMatch = payment.due_date ? checkDate(payment.due_date) : false;
    const createdMatch = payment.created_at ? checkDate(payment.created_at.substring(0, 10)) : false;

    return dueMatch || createdMatch;
  };

  // Computar pagos para el listado drilldown
  const drilldownPayments = useMemo(() => {
    if (!selectedCategory) return [];

    return payments.filter(p => {
      // 1. Moneda
      if ((p.currency || 'CRC') !== activeCurrency) return false;

      // 2. Categoría
      const category = (p.spending_category || 'OTHERS').toUpperCase().trim();
      if (category !== selectedCategory) return false;

      // 3. Correspondencia temporal
      return matchesVisualTimeRange(p, timeRange);
    });
  }, [payments, selectedCategory, activeCurrency, timeRange]);

  const handleClickCategory = (categoryKey) => {
    if (selectedCategory === categoryKey) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryKey);
    }
  };

  // Tooltip personalizado minimalista
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: '#1a202c', border: '1px solid #4a5568', padding: '12px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <p style={{ color: '#e2e8f0', margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '0.9em' }}>{payload[0].name}</p>
          <p style={{ color: '#fff', margin: 0, fontSize: '1.1em' }}>
            {formatCurrency(payload[0].value, activeCurrency, i18n.language)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ background: '#2d3748', padding: '25px', borderRadius: '12px', marginTop: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
      <h3 style={{ color: '#fff', textAlign: 'center', marginBottom: '30px', marginTop: '0', fontWeight: '600' }}>{t('analytics.spending_breakdown')}</h3>

      {/* Selector de Moneda */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', gap: '10px' }}>
        {availableCurrencies.map(curr => (
          <button
            key={curr}
            onClick={() => setActiveCurrency(curr)}
            style={{
              background: activeCurrency === curr ? '#3b82f6' : 'transparent',
              color: activeCurrency === curr ? '#fff' : '#a0aec0',
              border: activeCurrency === curr ? 'none' : '1px solid #4a5568',
              padding: '6px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.9em',
              transition: 'all 0.2s'
            }}
          >
            {curr}
          </button>
        ))}
      </div>

      {/* Selector de Filtro de Tiempo */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{
            background: '#1a202c',
            color: '#e2e8f0',
            border: '1px solid #4a5568',
            padding: '8px 16px',
            borderRadius: '8px',
            outline: 'none',
            cursor: 'pointer',
            fontSize: '0.95em',
            fontWeight: '500'
          }}
        >
          <option value="all">Todo el Historial</option>
          <option value="this_month">Este Mes</option>
          <option value="last_month">Mes Pasado</option>
        </select>
      </div>

      {data.length > 0 ? (
        <>
          <div style={{ height: '300px', width: '100%', minWidth: 0, minHeight: '250px', position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={250}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={85}
                  outerRadius={105}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                  cornerRadius={4}
                  isAnimationActive={true}
                  onClick={(entry) => {
                    if (entry) {
                      const catKey = entry.categoryKey || (entry.payload && entry.payload.categoryKey);
                      if (catKey) {
                        handleClickCategory(catKey);
                      }
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {data.map((entry, index) => {
                    const isSelected = !selectedCategory || selectedCategory === entry.categoryKey;
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        style={{
                          opacity: isSelected ? 1 : 0.4,
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                      />
                    );
                  })}
                </Pie>
                <Tooltip content={<CustomTooltip />} cursor={false} />
              </PieChart>
            </ResponsiveContainer>
            {/* Texto central opcional con el total */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <p style={{ margin: 0, color: '#a0aec0', fontSize: '0.8em' }}>Total</p>
              <p style={{ margin: 0, color: '#fff', fontWeight: 'bold', fontSize: '1.1em' }}>{formatCurrency(total, activeCurrency, i18n.language)}</p>
            </div>
          </div>

          {/* Leyenda Personalizada Debajo */}
          <div style={{ marginTop: '30px', borderTop: '1px solid #4a5568', paddingTop: '20px' }}>
            {data.map((entry, index) => {
              const isSelected = !selectedCategory || selectedCategory === entry.categoryKey;
              return (
                <div
                  key={index}
                  onClick={() => handleClickCategory(entry.categoryKey)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    fontSize: '0.95em',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: selectedCategory === entry.categoryKey ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                    transition: 'all 0.2s',
                    opacity: isSelected ? 1 : 0.4
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS[index % COLORS.length], marginRight: '12px' }}></div>
                    <span style={{ color: '#e2e8f0' }}>{entry.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ color: '#a0aec0', marginRight: '15px' }}>{formatCurrency(entry.value, activeCurrency, i18n.language)}</span>
                    <span style={{ color: '#fff', fontWeight: 'bold', minWidth: '40px', textAlign: 'right' }}>{((entry.value / total) * 100).toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sub-tabla Drilldown Dinámica */}
          {selectedCategory && (
            <div style={{ marginTop: '25px', background: 'rgba(0, 0, 0, 0.2)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ color: '#fff', margin: 0, fontSize: '1em', fontWeight: '600' }}>
                  {t('analytics.details_for')}: {t(`categories.${selectedCategory}`, selectedCategory)}
                </h4>
                <button
                  onClick={() => setSelectedCategory(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#f87171',
                    cursor: 'pointer',
                    fontSize: '0.85em',
                    fontWeight: '600',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(248, 113, 113, 0.1)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  {t('analytics.clear_filter')}
                </button>
              </div>

              {drilldownPayments.length > 0 ? (
                <div style={{ overflowX: 'auto', borderRadius: '8px' }}>
                  <table className="min-w-full divide-y divide-gray-200" style={{ borderCollapse: 'collapse', textAlign: 'left', width: '100%' }}>
                    <thead>
                      <tr style={{ color: '#a0aec0', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <th style={{ padding: '10px 12px', fontWeight: '600' }}>{t('analytics.date')}</th>
                        <th style={{ padding: '10px 12px', fontWeight: '600' }}>{t('analytics.biller')}</th>
                        <th style={{ padding: '10px 12px', fontWeight: '600', textAlign: 'right' }}>{t('analytics.amount')}</th>
                        <th style={{ padding: '10px 12px', fontWeight: '600', textAlign: 'center' }}>{t('analytics.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700" style={{ color: '#e2e8f0', fontSize: '0.9em' }}>
                      {drilldownPayments.map((payment) => (
                        <tr key={payment.id} style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                            {(() => {
                              try {
                                const dateStr = payment.due_date || (payment.created_at ? payment.created_at.substring(0, 10) : null);
                                if (!dateStr) return t('app.na');
                                const datePart = parseIsoDate(dateStr);
                                return new Date(`${datePart}T00:00:00`).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
                              } catch (e) {
                                return payment.due_date || t('app.na');
                              }
                            })()}
                          </td>
                          <td style={{ padding: '12px', fontWeight: '500', color: '#fff' }}>{payment.biller_name}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            {formatCurrency(payment.amount, payment.currency, i18n.language)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '0.8em',
                              fontWeight: '600',
                              background: payment.is_paid ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                              color: payment.is_paid ? '#34d399' : '#fbbf24',
                              border: `1px solid ${payment.is_paid ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                            }}>
                              {payment.is_paid ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#a0aec0', fontSize: '0.9em', fontStyle: 'italic', margin: '10px 0 0 0' }}>
                  No payments found for this category and selected period.
                </p>
              )}
            </div>
          )}

          {/* Sección de Insights de IA con Advertencia Global */}
          {insightText && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '8px 12px', borderRadius: '8px', marginBottom: '-15px', position: 'relative', zIndex: 1, border: '1px solid rgba(255,255,255,0.1)', borderBottom: 'none', width: 'fit-content', marginLeft: '20px', fontSize: '0.75em', color: '#a0aec0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Overall Account Analysis
              </div>
              <AIInsightCard insightText={insightText} />
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#a0aec0' }}>
          <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>{t('analytics.no_recent_payments')}</p>
        </div>
      )}
    </div>
  );
};

export default SpendingBreakdown;